import React, { useState, useEffect } from 'react';
import { withTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  Link,
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  SwapVert as SwapVertIcon,
  Inventory as InventoryIcon,
  Undo as UndoIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayArrowIcon,
  Launch as LaunchIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useUserStore } from 'store';
import { formatQuantity, formatQuantityChange } from '../../utils';

const transactionTypeConfig = {
  initial: { color: 'default', icon: PlayArrowIcon },
  purchase: { color: 'success', icon: AddIcon },
  sale: { color: 'error', icon: RemoveIcon },
  adjustment: { color: 'warning', icon: SwapVertIcon },
  count: { color: 'info', icon: InventoryIcon },
  return: { color: 'success', icon: UndoIcon },
  damaged: { color: 'error', icon: DeleteIcon },
  parts_sale: { color: 'error', icon: RemoveIcon },
};

function InventoryTransactionsDialog({ t, open, onClose, inventoryItem }) {
  const { currentOrganization } = useUserStore();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && inventoryItem) {
      fetchTransactions();
    }
  }, [open, inventoryItem]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(
        `/api/inventory/${inventoryItem.id}/transactions/?organization=${currentOrganization.organization_id}`
      );
      // Handle both paginated ({results: [...]}) and non-paginated ([...]) responses
      const data = response.data;
      setTransactions(Array.isArray(data) ? data : data.results || []);
    } catch (err) {
      console.error('Failed to fetch transactions:', err);
      setError(t('Failed to load transaction history'));
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Intl.DateTimeFormat('fi-FI', {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(new Date(dateStr));
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('fi-FI', {
      style: 'currency',
      currency: 'EUR',
    }).format(value);
  };

  const getTransactionTypeLabel = (type) => {
    return t(`inventory.transactionTypes.${type}`, { defaultValue: type });
  };

  if (!inventoryItem) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth data-testid="inventory-transactions-dialog">
      <DialogTitle>
        {t('inventory.transactionHistory')}
        <Typography variant="body2" color="text.secondary">
          {inventoryItem.part_name}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : transactions.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <InventoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">
              {t('inventory.noTransactions')}
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" sx={{ tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 120 }}>{t('Date')}</TableCell>
                  <TableCell sx={{ width: 140 }}>{t('Type')}</TableCell>
                  <TableCell align="right" sx={{ width: 70 }}>{t('Change')}</TableCell>
                  <TableCell align="right" sx={{ width: 70 }}>{t('After')}</TableCell>
                  <TableCell sx={{ width: 90 }}>{t('inventory.maintenanceLink')}</TableCell>
                  <TableCell align="right" sx={{ width: 110 }}>{t('inventory.purchaseCost')} ({t('excl. VAT')})</TableCell>
                  <TableCell align="right" sx={{ width: 90 }}>{t('inventory.sellingPrice')}</TableCell>
                  <TableCell sx={{ width: 120 }}>{t('User')}</TableCell>
                  <TableCell>{t('Notes')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.map((txn) => {
                  const config = transactionTypeConfig[txn.transaction_type] || { color: 'default' };
                  const Icon = config.icon;
                  const change = parseFloat(txn.quantity_change);

                  return (
                    <TableRow key={txn.id} hover>
                      <TableCell>
                        <Typography variant="body2" noWrap>
                          {formatDateTime(txn.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={getTransactionTypeLabel(txn.transaction_type)}
                          color={config.color}
                          icon={Icon ? <Icon /> : undefined}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: change >= 0 ? 'success.main' : 'error.main',
                          }}
                        >
                          {formatQuantityChange(txn.quantity_change)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {formatQuantity(txn.quantity_after)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {txn.maintenance_id ? (
                          <Link
                            component={RouterLink}
                            to={`/maintenances/${txn.maintenance_id}`}
                            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                            onClick={onClose}
                          >
                            {txn.maintenance_work_number}
                            <LaunchIcon sx={{ fontSize: 14 }} />
                          </Link>
                        ) : (
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {txn.reference_number || '-'}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">
                          {txn.transaction_type === 'purchase' && txn.unit_cost
                            ? formatCurrency(txn.unit_cost)
                            : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">
                          {txn.transaction_type === 'sale' && txn.selling_price
                            ? formatCurrency(txn.selling_price)
                            : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" noWrap title={txn.user_name || txn.user_email || ''}>
                          {txn.user_name || txn.user_email || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={txn.notes}
                        >
                          {txn.notes || '-'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('Close')}</Button>
      </DialogActions>
    </Dialog>
  );
}

export default withTranslation()(InventoryTransactionsDialog);
